/** @jsx jsx */
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type {
  DropdownMenuContentProps,
  DropdownMenuProps,
} from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import type { WithDataAttributeProp } from '@balance-web/core';
import { getTestIdFromDataAttributes, jsx } from '@balance-web/core';
import { flattenElements } from '@balance-web/utils';

import { Trigger } from './radix-styled-components';
import { Content, Divider, SubMenuTrigger } from './radix-styled-components';
import { Item } from './Item';
import { RadioGroup } from './RadioGroup';
import { RadioItem } from './RadioItem';
import { GroupTitle } from './GroupTitle';
import { MenuTitle } from './MenuTitle';
import { SubMenu } from './SubMenu';
import { TriggerButton } from './TriggerButton';
import { TestIdContextProvider } from './context';
import { DropdownContext } from './context';
import { DEFAULT_ITEM_HEIGHT } from './constants';

export type DropdownProps = WithDataAttributeProp<
  {
    children: ReactNode;
    /** Vertical offset of the dropdown menu relative to the trigger */
    verticalOffset?: DropdownMenuContentProps['sideOffset'];
    /** Number of items to render a scroll bar appears */
    viewportItemCount?: number;
    itemHeight?: number;
    /**
     * Controls Radix's modal behaviour. Defaults to `false` (non-modal): the
     * page stays scrollable and interactive while the menu is open and no
     * scroll lock is applied.
     *
     * Pass `true` for the modal behaviour — a scroll lock plus disabled outside
     * pointer events and a focus trap while the menu is open.
     *
     * Non-modal is the default because the modal scroll lock briefly removes
     * the scrollbar and reflows the page, which can reposition the menu over
     * its trigger (e.g. inside a sticky page header) and select an item the
     * instant the menu opens.
     */
    modal?: DropdownMenuProps['modal'];
    /** Narrower or wider than the default 220px menu. */
    minWidth?: number | string;
  } & Pick<DropdownMenuContentProps, 'align'>
>;

export const Dropdown = ({
  children,
  verticalOffset,
  align,
  data,
  modal = false,
  ...props
}: DropdownProps) => {
  const childArray = flattenElements(children);

  /**
   * We render like this to reduce the number of primitives consumers have to deal with.
   * If, at some point doing this is costing us flexibility we might have to let consumers opt into
   * the Content primitive.
   */
  const triggerElement = childArray.find((child) => {
    return child.type === Trigger;
  });
  const nonTriggerElements = childArray.filter((child) => {
    return child.type !== Trigger;
  });

  const testId = getTestIdFromDataAttributes(data);

  return (
    <DropdownContext.Provider
      value={{ itemHeight: props.itemHeight || DEFAULT_ITEM_HEIGHT }}
    >
      <TestIdContextProvider value={testId}>
        <DropdownMenuPrimitive.Root modal={modal} {...props}>
          {triggerElement}
          <Content
            align={align}
            data={data}
            minWidth={props.minWidth}
            sideOffset={verticalOffset}
            viewportItemCount={props.viewportItemCount}
          >
            {nonTriggerElements}
          </Content>
        </DropdownMenuPrimitive.Root>
      </TestIdContextProvider>
    </DropdownContext.Provider>
  );
};
Dropdown.Trigger = Trigger;
Dropdown.TriggerButton = TriggerButton;
Dropdown.SubMenu = SubMenu;
Dropdown.SubMenuTrigger = SubMenuTrigger;
Dropdown.Item = Item;
Dropdown.RadioGroup = RadioGroup;
Dropdown.RadioItem = RadioItem;
Dropdown.Divider = Divider;
Dropdown.GroupTitle = GroupTitle;
Dropdown.MenuTitle = MenuTitle;
